// Spanish-language guides, served at /es/<slug>.
//
// Same shape as BLOG_POSTS in blogPosts.js, plus `translationOf`: the English
// slug this guide adapts. That pairing drives the hreflang links between the
// two pages and the "Leer en español" / "Read in English" switch, so every
// Spanish guide must point at a real English post (check-internal-links.mjs
// fails the build otherwise).
//
// FACTS: identical to the English post each adapts - every figure traces to the
// same IRS sources. TERMINOLOGY: from the IRS's own Spanish instructions,
// Instrucciones para el Formulario 2290(SP) (Rev. julio de 2026) - copy at
// Document/LLC/2290/irsforms-2290/sp/i2290sp.pdf. The filing app is English
// only, and every guide says so; none claims Spanish support.
//
// REVIEW: written 2026-10-07 and needs a native Spanish speaker's read before
// it is deployed (see the tracker).

import { RATES } from "./rates.js";

export const CATEGORIES_ES = {
  deadlines: "Fechas y multas",
  schedule1: "Anexo 1",
  amendments: "Enmiendas y correcciones",
  credits: "Créditos y reembolsos",
  basics: "Lo básico del Formulario 2290",
  rates: "Tarifas y categorías de peso",
  fleets: "Flotas y preparadores",
};

export const BLOG_POSTS_ES = [
// ES — translationOf: what-is-form-2290
  {
    slug: "formulario-2290",
    translationOf: "what-is-form-2290",
    category: "basics",
    title: "¿Qué es el Formulario 2290? El impuesto del IRS sobre vehículos pesados, explicado en español",
    metaTitle: "Formulario 2290 en español: qué es y cómo presentarlo",
    metaDescription:
      "El Formulario 2290 declara el impuesto federal sobre el uso de camiones de 55,000 libras o más. Quién lo presenta, cuánto cuesta, cuándo vence y cómo hacerlo.",
    keywords: [
      "formulario 2290",
      "formulario 2290 en español",
      "qué es el formulario 2290",
      "impuesto 2290 en español",
      "impuesto sobre el uso de vehículos pesados",
      "que es el 2290 en estados unidos",
    ],
    excerpt:
      "El Formulario 2290 es la declaración del IRS para el impuesto sobre el uso de vehículos pesados en las carreteras. Si su camión pesa 55,000 libras o más, es el trámite que necesita para registrar sus placas.",
    cta: {
      heading: "¿Listo para presentar su Formulario 2290?",
      text: "Presente por internet en una sola sesión. La aplicación está en inglés; nuestras guías en español le explican cada paso.",
      label: "Crear su cuenta gratis",
      to: "/login?tab=up",
    },
    publishedAt: "2026-10-07",
    updatedAt: "2026-10-07",
    readingMinutes: 6,
    irsVerified: true,
    sources:
      "Instrucciones para el Formulario 2290(SP) (Rev. julio de 2026) — Propósito del Formulario, Quién Tiene que Presentar el Formulario, Vehículos Tributables, Cuándo se Tiene que Presentar el Formulario, Cómo Presentar la Declaración, Cómo Comenzar, Anexo 1 del Formulario 2290; Formulario 2290, página 2",
    body: [
      {
        type: "callout",
        tone: "info",
        text: `En pocas palabras: el Formulario 2290 es la Declaración del Impuesto sobre el Uso de Vehículos Pesados en las Carreteras. Es un impuesto federal anual sobre camiones y otros vehículos con un peso bruto tributable de 55,000 libras o más. El período tributario va del 1 de julio al 30 de junio, el impuesto llega como máximo a $${RATES.V[0].toFixed(2)} por vehículo para un período completo, y la declaración aceptada le da un Anexo 1 estampado: el comprobante de pago que su estado le pide para registrar el camión.`,
      },
      {
        type: "p",
        text: "El Formulario 2290 es la declaración federal del impuesto sobre el uso de vehículos pesados en las carreteras públicas. Si el camión está registrado a su nombre y su peso lo hace tributable, presentarla no es opcional.",
      },
      {
        type: "p",
        text: "La mayoría de los camioneros llega al formulario por una razón práctica: el Departamento de Vehículos Motorizados (DMV) de su estado no registra ni renueva un vehículo pesado sin comprobante de que el impuesto se declaró, y ese comprobante sólo existe cuando la declaración se presentó y fue aceptada.",
      },
      { type: "h2", text: "Quién tiene que presentarlo" },
      {
        type: "p",
        text: "La obligación sigue al registro del vehículo, no a quién lo maneja. Si un vehículo pesado tributable está registrado a su nombre —como persona física, LLC, sociedad anónima o sociedad colectiva—, usted es quien presenta. Eso incluye al [propietario-operador con un solo camión](/es/formulario-2290-propietario-operador), a las flotas y a quien entra a los Estados Unidos con un vehículo pesado registrado en Canadá o México, que necesita el mismo comprobante en la frontera.",
      },
      {
        type: "p",
        text: "También necesita un número de identificación del empleador (EIN). El IRS no acepta un Formulario 2290 con su número de Seguro Social — vea [el EIN para el Formulario 2290](/es/ein-para-formulario-2290).",
      },
      { type: "h2", text: "Qué vehículos pagan" },
      {
        type: "p",
        text: "Un vehículo es tributable si es un vehículo motorizado de uso en carreteras con un peso bruto tributable de 55,000 libras o más. Por eso las camionetas tipo van y pickup por lo general no pagan este impuesto.",
      },
      {
        type: "p",
        text: "El peso bruto tributable no es el peso del camión vacío. Es el vehículo totalmente equipado para el servicio, más los remolques que normalmente usa con él, más la carga máxima que normalmente lleva. La tabla completa de categorías está en la [página de tarifas (en inglés)](/rates).",
      },
      {
        type: "cta",
        heading: "Calcule su impuesto antes de presentar",
        text: "Escoja la categoría de peso y el mes de primer uso. La calculadora está en inglés y usa las tablas del IRS.",
        label: "Abrir la calculadora (en inglés)",
        to: "/form-2290-tax-calculator",
      },
      { type: "h2", text: "El período tributario no es el año calendario" },
      {
        type: "p",
        text: "El período tributario del Formulario 2290 va del 1 de julio al 30 de junio. Y no hay una sola fecha límite para todos: la declaración vence el último día del mes siguiente al mes en que el vehículo se usó por primera vez en una carretera pública durante el período. Un camión que ya estaba trabajando en julio vence el 31 de agosto; uno que se usó por primera vez en noviembre vence el 31 de diciembre. Vea [todas las fechas de vencimiento](/es/fecha-de-vencimiento-formulario-2290).",
      },
      { type: "h2", text: "Cuánto cuesta" },
      {
        type: "p",
        text: "El impuesto depende de dos cosas: la categoría del peso bruto tributable y cuántos meses del período se usa el vehículo. Un vehículo que trabaja el período completo paga la cantidad anual de su categoría; uno que empieza a usarse después de julio paga sólo por los meses que quedan. Los vehículos madereros (que transportan productos cosechados en una zona de explotación forestal y están registrados así bajo la ley estatal) pagan tarifas reducidas en cada categoría.",
      },
      { type: "h2", text: "Qué recibe al presentar" },
      {
        type: "p",
        text: "Un [Anexo 1 estampado](/es/anexo-1-formulario-2290). Lista los VIN que usted declaró y lleva la marca del IRS que confirma que la declaración fue aceptada. Es el documento que el DMV le pide para registrar el camión. Si presenta electrónicamente, el IRS dice que el Anexo 1 estampado puede estar disponible dentro de unos minutos después de que acepte la declaración.",
      },
      { type: "h2", text: "Puede no deber nada y aun así tener que presentar" },
      {
        type: "p",
        text: "Un vehículo que se anticipa usará 5,000 millas o menos durante el período —7,500 millas o menos si es agropecuario— se declara como [vehículo suspendido del impuesto, categoría W](/es/vehiculos-suspendidos-categoria-w), sin impuesto a pagar. Suspendido quiere decir que no paga impuesto, no que no presenta: el vehículo se declara igual y aparece en su Anexo 1.",
      },
      { type: "h2", text: "En papel o por internet" },
      {
        type: "p",
        text: "La presentación electrónica es obligatoria si declara y paga impuesto por 25 vehículos o más. Además, según el IRS, sólo el Formulario 2290 en inglés se puede presentar electrónicamente. La aplicación para presentar está en inglés; la [guía paso a paso en español](/es/como-presentar-formulario-2290-en-linea) le explica cada pantalla. Cuando esté listo, puede [comenzar su Formulario 2290](/wizard/1).",
      },
    ],
    faqs: [
      {
        q: "¿Para qué sirve el Formulario 2290?",
        a: "Para declarar y pagar el impuesto federal sobre el uso de vehículos pesados en las carreteras, para vehículos con un peso bruto tributable de 55,000 libras o más. La declaración aceptada produce el Anexo 1 estampado que el DMV le pide para registrar un vehículo pesado.",
      },
      {
        q: "¿Quién tiene que presentar el Formulario 2290?",
        a: "Toda persona o empresa a cuyo nombre esté registrado un vehículo pesado tributable: propietarios-operadores, flotas, sociedades. Necesita un EIN; no se puede presentar con el número de Seguro Social.",
      },
      {
        q: "¿Tengo que presentar si mi camión casi no se usa?",
        a: "Sí. Un vehículo que se anticipa usará 5,000 millas o menos (7,500 si es agropecuario) se declara como suspendido, categoría W, sin impuesto, pero se tiene que declarar y aparece en su Anexo 1.",
      },
      {
        q: "¿Puedo presentar el Formulario 2290 en español?",
        a: "El IRS publica las instrucciones en español, pero indica que sólo el Formulario 2290 en inglés se puede presentar electrónicamente. Nuestra aplicación está en inglés y estas guías le explican cada paso en español.",
      },
    ],
  },

  // ES — translationOf: how-to-file-form-2290-online
  {
    slug: "como-presentar-formulario-2290-en-linea",
    translationOf: "how-to-file-form-2290-online",
    category: "basics",
    title: "Cómo presentar el Formulario 2290 por internet: guía paso a paso en español",
    metaTitle: "Cómo presentar el Formulario 2290 por internet, paso a paso",
    metaDescription:
      "Guía en español para presentar el Formulario 2290 por internet: qué necesita, qué significa cada pantalla en inglés, cómo pagar y cómo recibir su Anexo 1.",
    keywords: [
      "cómo presentar el formulario 2290",
      "presentar formulario 2290 por internet",
      "formulario 2290 en línea",
      "declarar 2290 en español",
      "e-file 2290 en español",
    ],
    excerpt:
      "El IRS anima a todos a presentar el Formulario 2290 electrónicamente, y lo exige con 25 vehículos o más. Aquí está el proceso completo, con lo que significa cada pantalla de la aplicación en inglés.",
    cta: {
      heading: "Comience su Formulario 2290",
      text: "La aplicación está en inglés; tenga esta guía abierta mientras presenta. Crear la cuenta es gratis; sólo paga cuando presenta.",
      label: "Crear su cuenta gratis",
      to: "/login?tab=up",
    },
    publishedAt: "2026-10-07",
    updatedAt: "2026-10-07",
    readingMinutes: 7,
    irsVerified: true,
    sources:
      "Instrucciones para el Formulario 2290(SP) (Rev. julio de 2026) — Cómo Presentar la Declaración, Cómo Comenzar, Número de Identificación del Empleador, Número de Identificación del Vehículo, Peso Bruto Tributable, Declaración enmendada, Corrección del VIN, Cómo Pagar el Impuesto, Anexo 1 del Formulario 2290, Cuándo se Tiene que Presentar el Formulario",
    body: [
      {
        type: "p",
        text: "El IRS anima a todos los contribuyentes a presentar el [Formulario 2290](/es/formulario-2290) por medios electrónicos, y lo exige para toda declaración que declare y pague impuesto por 25 vehículos o más. La razón práctica es la rapidez: una vez aceptada la declaración electrónica, el Anexo 1 estampado puede estar disponible dentro de unos minutos.",
      },
      {
        type: "callout",
        tone: "info",
        text: "Importante: según el IRS, sólo el Formulario 2290 en inglés se puede presentar electrónicamente, y nuestra aplicación está en inglés. Esta guía le explica cada pantalla en español; los nombres en inglés aparecen entre comillas tal como los verá.",
      },
      { type: "h2", text: "Antes de empezar: tres datos" },
      {
        type: "p",
        text: "Las instrucciones del IRS piden tener a mano:",
      },
      {
        type: "ul",
        items: [
          "Su número de identificación del empleador (EIN). No puede usar su número de Seguro Social — vea [el EIN para el Formulario 2290](/es/ein-para-formulario-2290).",
          "El número de identificación (VIN) de cada vehículo, del registro, del título o del vehículo mismo. Use el VIN del camión, no el del remolque.",
          "El peso bruto tributable de cada vehículo para determinar su categoría.",
        ],
      },
      {
        type: "p",
        text: "También necesita el mes en que cada camión se usó por primera vez en una carretera pública durante el período: ese mes fija la fecha de vencimiento y el impuesto. La [calculadora de fechas (en inglés)](/form-2290-due-date-calculator) le da el plazo de cualquier mes.",
      },
      {
        type: "cta",
        heading: "Cree su cuenta y siga esta guía",
        text: "Crear la cuenta es gratis. La aplicación está en inglés.",
        label: "Crear su cuenta gratis",
        to: "/login?tab=up",
      },
      { type: "h2", text: "Cada pantalla, en español" },
      {
        type: "table",
        headers: ["Pantalla o campo (en inglés)", "Qué significa"],
        rows: [
          ["\"Tax preparer\"", "Preparador de impuestos. Sólo si presenta por un cliente; si es su propio negocio, sáltelo."],
          ["\"Business\"", "Negocio: nombre legal (\"Business legal name\"), EIN (\"EIN (Employer ID Number)\"), tipo de negocio (\"Business type\"), dirección y teléfono."],
          ["\"Name control\"", "Código de control del nombre: los caracteres que el IRS tiene registrados con su EIN. Si no coinciden con el IRS, la declaración se rechaza."],
          ["\"Signing authority name\" / \"Title\"", "Nombre y cargo de la persona que firma la declaración."],
          ["\"Form\"", "Tipo de declaración: \"Form2290\" (declaración regular), \"Amended return\" (declaración enmendada) o \"VIN correction\" (corrección del VIN)."],
          ["\"Trucks\"", "Camiones: \"VIN\", \"Weight category\" (categoría de peso) y \"Month first used (this return)\" (mes de primer uso)."],
          ["\"Logging truck\" / \"Suspended (low mileage)\"", "Vehículo maderero (tarifa reducida) / vehículo suspendido del impuesto, categoría W."],
          ["\"Prior year\"", "Año anterior: vehículos que declaró como suspendidos el período pasado, y los que vendió."],
          ["\"Credits\"", "Créditos por un camión vendido, destruido o robado."],
          ["\"Tax summary\" / \"Review\"", "Resumen del impuesto y revisión de toda la declaración antes de pagar."],
          ["\"Pay\"", "Pago: \"EFW\" (retiro electrónico de fondos), \"EFTPS\" o \"Credit or debit card\" (tarjeta de crédito o débito)."],
        ],
      },
      { type: "h2", text: "Paso 1: Datos del negocio" },
      {
        type: "p",
        text: "Anote el nombre legal, el EIN, el tipo de negocio y la dirección. El EIN y el código de control del nombre tienen que coincidir con los registros del IRS. Aquí también indica quién firma la declaración.",
      },
      { type: "h2", text: "Paso 2: Tipo de declaración" },
      {
        type: "ul",
        items: [
          "\"Form2290\": la declaración regular para los vehículos que usó por primera vez en este período.",
          "\"Amended return\": sólo para un aumento en el peso bruto tributable o un vehículo suspendido que excedió el límite de millas.",
          "\"VIN correction\": para corregir un VIN en un Anexo 1 ya presentado — vea [la corrección del VIN](/es/correccion-de-vin-formulario-2290).",
        ],
      },
      { type: "h2", text: "Paso 3: Sus camiones" },
      {
        type: "p",
        text: "Escoja el mes de primer uso y agregue el VIN y la categoría de peso de cada camión. Marque los vehículos madereros y los suspendidos (5,000 millas o menos; 7,500 si es agropecuario) — vea [vehículos suspendidos, categoría W](/es/vehiculos-suspendidos-categoria-w). Si usó camiones por primera vez en meses distintos, el IRS exige una declaración separada por cada mes.",
      },
      { type: "h2", text: "Paso 4: Año anterior y créditos" },
      {
        type: "p",
        text: "Si declaró camiones suspendidos el período pasado, confirme que no pasaron del límite de millas. Luego reclame el crédito por un camión vendido, destruido o robado — vea [el crédito por un camión vendido, robado o destruido](/es/credito-camion-vendido-robado-destruido).",
      },
      { type: "h2", text: "Paso 5: Revise y pague" },
      {
        type: "p",
        text: "El resumen muestra el impuesto de cada camión según las tablas del IRS. Revise todo antes de pagar: un VIN mal escrito es el error más fácil de evitar. El impuesto se paga completo con la declaración, por retiro electrónico de fondos, EFTPS o tarjeta — vea [cómo pagar el Formulario 2290](/es/como-pagar-formulario-2290).",
      },
      { type: "h2", text: "Paso 6: Firme, transmita y descargue el Anexo 1" },
      {
        type: "p",
        text: "Firme electrónicamente y transmita. Una declaración sin firma no se considera presentada. Cuando el IRS la acepta, descargue su [Anexo 1 estampado](/es/anexo-1-formulario-2290). Con esta guía abierta, ya puede [comenzar su Formulario 2290](/wizard/1).",
      },
    ],
    faqs: [
      {
        q: "¿Cómo presento el Formulario 2290 por internet?",
        a: "A través de un proveedor autorizado del IRS. Anote los datos del negocio y el EIN, escoja el tipo de declaración, agregue el VIN, la categoría de peso y el mes de primer uso de cada camión, revise y pague el impuesto, y luego firme y transmita.",
      },
      {
        q: "¿Qué necesito para presentar el Formulario 2290?",
        a: "Su EIN, el VIN de cada vehículo y el peso bruto tributable de cada uno. También el mes de primer uso de cada camión en el período y una forma de pagar el impuesto.",
      },
      {
        q: "¿Se puede presentar el Formulario 2290 electrónicamente en español?",
        a: "No. Según las instrucciones del IRS, sólo el Formulario 2290 en inglés se puede presentar electrónicamente. Esta guía le explica en español cada pantalla de la aplicación en inglés.",
      },
      {
        q: "¿Tengo que presentar electrónicamente?",
        a: "Sólo si declara y paga impuesto por 25 vehículos o más. Los vehículos suspendidos (categoría W) no cuentan para esos 25. Los demás pueden presentar en papel, aunque el IRS anima a presentar electrónicamente.",
      },
    ],
  },

  // ES — translationOf: form-2290-due-date-2026-2027
  {
    slug: "fecha-de-vencimiento-formulario-2290",
    translationOf: "form-2290-due-date-2026-2027",
    category: "deadlines",
    title: "Fecha de vencimiento del Formulario 2290 para 2026–2027: todos los plazos por mes de primer uso",
    metaTitle: "Fecha de vencimiento del Formulario 2290 2026-2027",
    metaDescription:
      "¿Cuándo vence el Formulario 2290? El 31 de agosto de 2026 para camiones usados por primera vez en julio. Todos los plazos del período 2026-2027, mes por mes.",
    keywords: [
      "fecha de vencimiento formulario 2290",
      "cuándo vence el 2290",
      "fecha límite 2290 2026",
      "plazo formulario 2290",
      "2290 vencimiento agosto",
    ],
    excerpt:
      "Mucha gente cree que el Formulario 2290 siempre vence el 31 de agosto. Es cierto para la mayoría, pero no es la regla. La regla depende del mes en que su camión se usó por primera vez.",
    cta: {
      heading: "No deje pasar su fecha límite",
      text: "Presente por internet y reciba su Anexo 1 estampado. La aplicación está en inglés.",
      label: "Comenzar mi Formulario 2290",
      to: "/wizard/1",
    },
    publishedAt: "2026-10-07",
    updatedAt: "2026-10-07",
    readingMinutes: 5,
    irsVerified: true,
    sources:
      "Instrucciones para el Formulario 2290(SP) (Rev. julio de 2026) — Cuándo se Tiene que Presentar el Formulario (tabla y Ejemplos 1 a 3), Anexo 1 del Formulario 2290",
    body: [
      {
        type: "p",
        text: "La regla del IRS es esta: el [Formulario 2290](/es/formulario-2290) tiene que presentarse y el impuesto pagarse a más tardar el último día del mes siguiente al mes en que el vehículo se utilizó por primera vez en una carretera pública durante el período.",
      },
      {
        type: "p",
        text: "El 31 de agosto es sólo el plazo de los camiones que ya trabajaban en julio, el primer mes del período, y esos son la mayoría. Si puso un camión en servicio en noviembre, su plazo no es el 31 de agosto: es el 31 de diciembre.",
      },
      { type: "h2", text: "El período 2026–2027" },
      {
        type: "p",
        text: "El período comienza el 1 de julio de 2026 y termina el 30 de junio de 2027. No sigue el año calendario. Todo vehículo en la carretera durante ese tiempo se declara contra este período, incluidos los [vehículos suspendidos que usará 5,000 millas o menos](/es/vehiculos-suspendidos-categoria-w), que no pagan impuesto.",
      },
      { type: "h2", text: "Todas las fechas de vencimiento" },
      {
        type: "p",
        text: "Busque a la izquierda el mes en que su vehículo se usó por primera vez. Si la fecha cae en sábado, domingo o día de fiesta oficial, el plazo pasa al próximo día laborable; la tabla del IRS ya lo aplica.",
      },
      {
        type: "table",
        headers: ["Vehículo usado por primera vez en", "Presente y pague a más tardar el"],
        rows: [
          ["julio de 2026", "31 de agosto de 2026"],
          ["agosto de 2026", "30 de septiembre de 2026"],
          ["septiembre de 2026", "2 de noviembre de 2026"],
          ["octubre de 2026", "30 de noviembre de 2026"],
          ["noviembre de 2026", "31 de diciembre de 2026"],
          ["diciembre de 2026", "1 de febrero de 2027"],
          ["enero de 2027", "1 de marzo de 2027"],
          ["febrero de 2027", "31 de marzo de 2027"],
          ["marzo de 2027", "30 de abril de 2027"],
          ["abril de 2027", "1 de junio de 2027"],
          ["mayo de 2027", "30 de junio de 2027"],
          ["junio de 2027", "2 de agosto de 2027"],
        ],
      },
      {
        type: "callout",
        tone: "info",
        text: "El plazo no depende de su registro estatal. El IRS es claro: sin importar la fecha de renovación del registro, el Formulario 2290 vence el último día del mes siguiente al mes de primer uso.",
      },
      {
        type: "cta",
        heading: "¿Ya sabe su fecha?",
        text: "Presente hoy y tenga su Anexo 1 antes de ir al DMV. La aplicación está en inglés; esta guía en español le ayuda.",
        label: "Crear su cuenta gratis",
        to: "/login?tab=up",
      },
      { type: "h2", text: "Qué significa \"primer uso\"" },
      {
        type: "p",
        text: "Es el mes en que el vehículo se manejó por primera vez en una carretera pública durante este período. No es el mes en que lo compró ni el mes en que lo registró. El ejemplo del IRS: quien compra un camión y lo maneja a su casa desde el distribuidor ya hizo su primer uso tributable.",
      },
      {
        type: "p",
        text: "Si el camión ya trabajaba en el período anterior, su mes de primer uso es julio, el primer mes del nuevo período.",
      },
      { type: "h2", text: "Una declaración por cada mes" },
      {
        type: "p",
        text: "Si usa vehículos por primera vez en más de un mes, el IRS pide un Formulario 2290 separado por cada mes. Su ejemplo: los camiones usados por primera vez en julio se declaran a más tardar el 31 de agosto de 2026, y los de agosto en otra declaración a más tardar el 30 de septiembre de 2026.",
      },
      { type: "h2", text: "El mes de primer uso también cambia el impuesto" },
      {
        type: "p",
        text: "Un vehículo usado por primera vez en julio paga el período completo; uno usado después paga sólo los meses que quedan. Vea el monto con la [calculadora del impuesto (en inglés)](/form-2290-tax-calculator), o la guía completa en [cómo presentar por internet](/es/como-presentar-formulario-2290-en-linea).",
      },
      { type: "h2", text: "Presente temprano" },
      {
        type: "p",
        text: "El período empieza el 1 de julio y desde ese día puede presentar. Su [Anexo 1 estampado](/es/anexo-1-formulario-2290) es lo que el DMV le pide para registrar o renovar; si espera hasta el último día, no le queda margen.",
      },
    ],
    faqs: [
      {
        q: "¿El Formulario 2290 siempre vence el 31 de agosto?",
        a: "No. El 31 de agosto es el plazo de los vehículos usados por primera vez en julio. Si su vehículo se usó por primera vez en otro mes, el plazo es el último día del mes siguiente.",
      },
      {
        q: "¿Cuál es la fecha límite para el período 2026-2027?",
        a: "Para vehículos usados por primera vez en julio de 2026, el 31 de agosto de 2026. El período va del 1 de julio de 2026 al 30 de junio de 2027, y cada mes de primer uso tiene su propio plazo.",
      },
      {
        q: "¿Qué pasa si la fecha cae en fin de semana?",
        a: "Si la fecha cae en sábado, domingo o día de fiesta oficial, presente el próximo día laborable. La tabla del IRS ya tiene aplicados esos cambios.",
      },
      {
        q: "¿La fecha límite depende de mi registro estatal?",
        a: "No. El IRS indica que el plazo no está atado a la fecha de registro del vehículo; depende sólo del mes de primer uso.",
      },
    ],
  },

  // ES — translationOf: stamped-schedule-1-proof-of-payment
  {
    slug: "anexo-1-formulario-2290",
    translationOf: "stamped-schedule-1-proof-of-payment",
    category: "schedule1",
    title: "Anexo 1 del Formulario 2290 estampado: su comprobante de pago del IRS",
    metaTitle: "Anexo 1 (Formulario 2290) estampado: comprobante de pago",
    metaDescription:
      "El Anexo 1 estampado es el comprobante de pago del impuesto 2290 que piden el DMV y la frontera. Qué es, cuánto tarda, qué hacer si lo pierde y cómo obtenerlo.",
    keywords: [
      "anexo 1 formulario 2290",
      "schedule 1 2290 en español",
      "comprobante de pago 2290",
      "anexo 1 estampado",
      "2290 para registrar camión",
    ],
    excerpt:
      "Casi nadie presenta el Formulario 2290 porque quiere pagar el impuesto: lo presenta porque necesita el Anexo 1 estampado. Sin él, el camión no se registra.",
    cta: {
      heading: "Obtenga su Anexo 1 estampado",
      text: "Presente por internet y descárguelo cuando el IRS acepte su declaración. La aplicación está en inglés.",
      label: "Comenzar mi Formulario 2290",
      to: "/wizard/1",
    },
    publishedAt: "2026-10-07",
    updatedAt: "2026-10-07",
    readingMinutes: 5,
    irsVerified: true,
    sources:
      "Instrucciones para el Formulario 2290(SP) (Rev. julio de 2026) — Anexo 1 del Formulario 2290 (Sistema electrónico e-file; Verificación de pago para el registro estatal y entrada en los Estados Unidos), Cómo Presentar la Declaración",
    body: [
      {
        type: "p",
        text: "El Anexo 1 (Formulario 2290) es parte de la declaración. Lista, por categoría, los números de identificación (VIN) de los vehículos que declara. Cuando el IRS acepta la declaración, le devuelve una copia estampada: esa copia es su comprobante de que el impuesto de esos VIN se declaró y se pagó.",
      },
      { type: "h2", text: "Para qué lo necesita" },
      {
        type: "ul",
        items: [
          "Registro estatal: los estados generalmente piden verificación del pago del impuesto antes de registrar un vehículo tributable. Se usa la copia estampada del Anexo 1.",
          "Entrada a los Estados Unidos: el Servicio de Aduanas y Control de Fronteras pide el mismo comprobante para introducir un vehículo canadiense o mexicano.",
        ],
      },
      { type: "h2", text: "Cuánto tarda" },
      {
        type: "p",
        text: "Si presenta electrónicamente, el IRS dice que el Anexo 1 estampado puede estar disponible dentro de unos minutos después de que acepte la declaración. Lleva una filigrana (marca de agua) del IRS en vez de un sello de tinta, y el IRS se la envía electrónicamente al proveedor; usted la descarga de su cuenta.",
      },
      {
        type: "p",
        text: "En papel, tiene que esperar a que el IRS se lo devuelva por correo. Si su renovación está cerca, el papel no es una opción realista.",
      },
      {
        type: "cta",
        heading: "¿Necesita el Anexo 1 para el DMV?",
        text: "Presente por internet y descárguelo en cuanto el IRS acepte. La aplicación está en inglés; esta guía en español le ayuda.",
        label: "Crear su cuenta gratis",
        to: "/login?tab=up",
      },
      { type: "h2", text: "Casos que acepta el estado" },
      {
        type: "ul",
        items: [
          "Si el estado recibe su solicitud de registro en julio, agosto o septiembre, puede presentar el Anexo 1 aprobado del período inmediatamente anterior. Igual tiene que presentar el Formulario 2290 del período en vigencia a tiempo.",
          "Para un vehículo nuevo o de segunda mano comprado en los últimos 60 días, no se requiere verificación de pago si presenta el comprobante de venta. Aun así tiene que presentar la declaración y pagar el impuesto.",
          "Si no tiene la copia estampada, puede usar una fotocopia del Formulario 2290 con el Anexo 1 que presentó y una fotocopia de ambas caras del cheque cancelado.",
        ],
      },
      {
        type: "p",
        text: "Algunos estados participan con el IRS en un programa alterno de verificación de pago: el DMV le remite su Formulario 2290 al IRS. Pero la declaración no se considera presentada hasta que el IRS la recibe, y usted responde por multas o intereses si el DMV la presenta tarde o la pierde. Presentar usted mismo antes de la [fecha de vencimiento](/es/fecha-de-vencimiento-formulario-2290) evita ese riesgo.",
      },
      { type: "h2", text: "Si pierde su Anexo 1" },
      {
        type: "p",
        text: "No vuelva a presentar la declaración. Si presentó electrónicamente aquí, la copia queda guardada en su cuenta y la puede descargar otra vez. Para una copia del Anexo 1 de un período anterior, el IRS pide una solicitud por escrito a: Internal Revenue Service, 7940 Kentucky Drive, Florence, KY 41042-2915.",
      },
      { type: "h2", text: "Revíselo antes de transmitir" },
      {
        type: "p",
        text: "El IRS estampa lo que usted envió; no corrige el VIN. Un solo carácter equivocado produce un Anexo 1 que no coincide con el camión y el DMV lo rechaza. Tiene arreglo —vea [la corrección del VIN](/es/correccion-de-vin-formulario-2290)—, pero revisar antes es más rápido. Los [vehículos suspendidos](/es/vehiculos-suspendidos-categoria-w) también aparecen en el Anexo 1, así que inclúyalos.",
      },
    ],
    faqs: [
      {
        q: "¿Qué es el Anexo 1 estampado?",
        a: "Es la parte del Formulario 2290 que lista los VIN de sus vehículos por categoría, devuelta con la marca del IRS después de aceptar la declaración. Es su comprobante de que el impuesto se declaró y pagó.",
      },
      {
        q: "¿Cuánto tarda el Anexo 1 si presento por internet?",
        a: "El IRS indica que el Anexo 1 estampado puede estar disponible dentro de unos minutos después de aceptar una declaración electrónica.",
      },
      {
        q: "¿Qué hago si perdí mi Anexo 1?",
        a: "No vuelva a presentar. Si presentó electrónicamente aquí, descárguelo de nuevo de su cuenta. Para una copia de un período anterior, el IRS pide una solicitud por escrito a su oficina de Florence, KY.",
      },
      {
        q: "¿Puedo registrar mi camión en julio con el Anexo 1 del año pasado?",
        a: "Si el estado recibe la solicitud en julio, agosto o septiembre, el IRS permite usar el Anexo 1 aprobado del período anterior. Aun así debe presentar el Formulario 2290 del período en vigencia a tiempo.",
      },
    ],
  },

  // ES — translationOf: form-2290-vin-correction
  {
    slug: "correccion-de-vin-formulario-2290",
    translationOf: "form-2290-vin-correction",
    category: "amendments",
    title: "Corrección del VIN en el Formulario 2290: cómo arreglar un VIN equivocado",
    metaTitle: "Corrección del VIN en el Formulario 2290 | Cómo arreglarlo",
    metaDescription:
      "¿Su Anexo 1 tiene un VIN equivocado? La corrección del VIN del Formulario 2290 lo arregla sin volver a pagar el impuesto. Cuándo usarla y cómo presentarla.",
    keywords: [
      "corrección del vin 2290",
      "corregir vin formulario 2290",
      "vin equivocado anexo 1",
      "2290 vin correction en español",
    ],
    excerpt:
      "Un VIN tiene 17 caracteres. Cambiar dos de lugar o confundir un 8 con una B produce un Anexo 1 que no coincide con su camión. La corrección del VIN lo arregla sin volver a pagar.",
    cta: {
      heading: "Corrija su VIN",
      text: "Escoja \"VIN correction\" como tipo de declaración. La aplicación está en inglés.",
      label: "Comenzar la corrección",
      to: "/wizard/1",
    },
    publishedAt: "2026-10-07",
    updatedAt: "2026-10-07",
    readingMinutes: 5,
    irsVerified: true,
    sources:
      "Instrucciones para el Formulario 2290(SP) (Rev. julio de 2026) — Corrección del número de identificación del vehículo (VIN), Declaración enmendada, Número de Identificación del Vehículo (VIN)",
    body: [
      {
        type: "p",
        text: "El IRS estampa exactamente lo que usted envió: no compara el VIN con el vehículo. El DMV sí lo compara, y ahí es donde casi siempre aparece el error, cuando le rechazan el registro.",
      },
      { type: "h2", text: "Qué es una corrección del VIN" },
      {
        type: "p",
        text: "Es un Formulario 2290 con el recuadro Corrección del VIN marcado, para corregir un VIN listado en un Anexo 1 que ya presentó. No vuelve a pagar el impuesto: ya se pagó por ese vehículo. Usted anota el VIN correcto en el Anexo 1 y adjunta una explicación de la corrección. El resultado es un nuevo [Anexo 1 estampado](/es/anexo-1-formulario-2290) con el VIN correcto.",
      },
      {
        type: "p",
        text: "El IRS pide usar el Formulario 2290 del período tributario que está corrigiendo, y marcar ese recuadro sólo para corregir un VIN.",
      },
      { type: "h2", text: "Cuándo la necesita" },
      {
        type: "ul",
        items: [
          "El VIN de su declaración aceptada tiene un error: un carácter equivocado, de más, de menos o cambiado de lugar.",
          "El DMV rechazó su Anexo 1 porque el VIN no coincide con el vehículo.",
          "Usted mismo encontró el error después de que el IRS aceptó la declaración.",
        ],
      },
      {
        type: "cta",
        heading: "¿Le rechazaron el registro por el VIN?",
        text: "Presente la corrección por internet y descargue el nuevo Anexo 1. La aplicación está en inglés.",
        label: "Crear su cuenta gratis",
        to: "/login?tab=up",
      },
      { type: "h2", text: "Cuándo necesita otra cosa" },
      {
        type: "ul",
        items: [
          "Si el peso bruto tributable aumentó: eso es una declaración enmendada, con impuesto adicional.",
          "Si un [vehículo suspendido](/es/vehiculos-suspendidos-categoria-w) pasó el límite de millas: también es una declaración enmendada.",
          "Si su declaración fue rechazada (no aceptada): no hay nada que corregir, porque no quedó presentada. Corrija el VIN y vuelva a transmitir.",
        ],
      },
      { type: "h2", text: "Qué necesita para presentarla" },
      {
        type: "p",
        text: "El VIN equivocado tal como aparece en la declaración aceptada, el VIN correcto, y los datos de la declaración original: el EIN del negocio, el período tributario y el mes de primer uso. La corrección tiene que apuntar a la declaración original, así que esos datos tienen que coincidir con ella. En la aplicación, escoja \"VIN correction\" en la pantalla \"Form\".",
      },
      { type: "h2", text: "Cómo evitarla" },
      {
        type: "ul",
        items: [
          "Copie el VIN del registro, del título o del vehículo mismo, no de memoria. Use el VIN del camión, no el del remolque.",
          "Cuidado con los caracteres parecidos: 8 y B, 0 y D, 5 y S, 2 y Z. Un VIN nunca lleva las letras I, O ni Q.",
          "Cuente los caracteres: por lo general, un VIN tiene 17.",
          "Revise la lista de VIN en el resumen antes de transmitir.",
        ],
      },
      {
        type: "p",
        text: "Para presentar desde cero, siga [la guía paso a paso](/es/como-presentar-formulario-2290-en-linea). Si son varios VIN, la carga masiva desde Excel se explica en [la guía para flotas (en inglés)](/blog/bulk-form-2290-filing-for-fleets).",
      },
    ],
    faqs: [
      {
        q: "¿Puedo corregir un VIN después de presentar el Formulario 2290?",
        a: "Sí. Presente un Formulario 2290 con el recuadro Corrección del VIN marcado, anote el VIN correcto en el Anexo 1 y adjunte una explicación. Recibe un nuevo Anexo 1 estampado.",
      },
      {
        q: "¿Tengo que pagar el impuesto otra vez?",
        a: "No. El impuesto de ese vehículo ya se pagó; la corrección sólo cambia el VIN.",
      },
      {
        q: "¿Y si mi declaración fue rechazada?",
        a: "Entonces no hay nada que corregir, porque no quedó presentada. Arregle el VIN y vuelva a transmitir la declaración.",
      },
      {
        q: "¿La corrección del VIN sirve para cambiar la categoría de peso?",
        a: "No. Sólo cambia el VIN. Un aumento en el peso bruto tributable se declara con una declaración enmendada.",
      },
    ],
  },

  // ES — translationOf: suspended-vehicles-category-w
  {
    slug: "vehiculos-suspendidos-categoria-w",
    translationOf: "suspended-vehicles-category-w",
    category: "basics",
    title: "Vehículos suspendidos del impuesto y la categoría W: la regla de las 5,000 millas",
    metaTitle: "Vehículos suspendidos, categoría W: regla de 5,000 millas",
    metaDescription:
      "Un camión que recorrerá 5,000 millas o menos (7,500 si es agropecuario) no paga el impuesto 2290, pero sí se declara. Cómo funciona la categoría W.",
    keywords: [
      "categoría w 2290",
      "vehículo suspendido 2290",
      "5000 millas 2290",
      "7500 millas agropecuario 2290",
      "camión poco uso 2290",
    ],
    excerpt:
      "Un camión que casi no trabaja durante el período puede declararse como suspendido, categoría W, y no pagar nada. Pero \"no pagar\" no quiere decir \"no presentar\".",
    cta: {
      heading: "Declare sus vehículos suspendidos",
      text: "Marque \"Suspended (low mileage)\" en la pantalla de camiones. La aplicación está en inglés.",
      label: "Comenzar mi Formulario 2290",
      to: "/wizard/1",
    },
    publishedAt: "2026-10-07",
    updatedAt: "2026-10-07",
    readingMinutes: 5,
    irsVerified: true,
    sources:
      "Instrucciones para el Formulario 2290(SP) (Rev. julio de 2026) — Propósito del Formulario, Parte II (Líneas 7 a 9), Vehículos suspendidos que exceden el límite sobre las millas que se pueden usar, Vehículos agropecuarios, Cómo Presentar la Declaración, Registros",
    body: [
      {
        type: "callout",
        tone: "info",
        text: "Suspendido quiere decir que no se paga impuesto. No quiere decir que no se presenta: un vehículo de categoría W se tiene que declarar en el Formulario 2290.",
      },
      { type: "h2", text: "El límite de millas" },
      {
        type: "p",
        text: "Un vehículo que se anticipa usará 5,000 millas o menos en las carreteras públicas durante el período tributario puede declararse como suspendido del impuesto. Para vehículos agropecuarios el límite es 7,500 millas o menos.",
      },
      {
        type: "p",
        text: "El límite se aplica a todo el período —del 1 de julio al 30 de junio— y al total de millas del vehículo durante el período, sin importar cuántos dueños haya tenido.",
      },
      { type: "h2", text: "Es un pronóstico, y usted responde por él" },
      {
        type: "p",
        text: "Usted declara la suspensión al presentar, según lo que espera que haga el vehículo. Si se queda dentro del límite, no tiene que hacer nada más. Si lo excede, el impuesto se vuelve exigible: presenta una declaración enmendada, con el impuesto calculado según el mes de primer uso del vehículo, a más tardar el último día del mes siguiente al mes en que se excedió el límite. Nadie le avisa: llevar la cuenta de las millas es responsabilidad suya.",
      },
      {
        type: "cta",
        heading: "¿Tiene camiones con poco millaje?",
        text: "Declárelos como suspendidos y obtenga su Anexo 1. La aplicación está en inglés; esta guía le explica cada paso.",
        label: "Crear su cuenta gratis",
        to: "/login?tab=up",
      },
      { type: "h2", text: "Vehículos agropecuarios" },
      {
        type: "p",
        text: "El límite de 7,500 millas es sólo para vehículos agropecuarios: los que se usan mayormente para fines agropecuarios y están registrados así bajo la ley estatal durante todo el período. Las millas recorridas dentro de la granja no cuentan para el límite, pero el IRS pide llevar un registro exacto de ellas. Confirme que el vehículo califica antes de usar el límite más alto.",
      },
      { type: "h2", text: "La categoría W y la regla de los 25 vehículos" },
      {
        type: "p",
        text: "La presentación electrónica es obligatoria cuando declara y paga impuesto por 25 vehículos o más. Los vehículos suspendidos no cuentan para esos 25, porque no se paga impuesto por ellos. Una flota con 20 camiones tributables y 10 suspendidos cuenta 20, no 30.",
      },
      { type: "h2", text: "Los suspendidos aparecen en su Anexo 1" },
      {
        type: "p",
        text: "Como se declaran, los vehículos de categoría W se listan en su [Anexo 1 estampado](/es/anexo-1-formulario-2290) junto con los tributables. Un vehículo suspendido que no se declaró no aparece, y eso puede detener el registro de un camión que no debía nada.",
      },
      { type: "h2", text: "El período siguiente" },
      {
        type: "p",
        text: "En el Formulario 2290 del período siguiente, el IRS le pide verificar que los vehículos que declaró suspendidos no pasaron del límite, y listar los que sí. Si vendió un vehículo suspendido, al comprador se le entrega una declaración por escrito con los datos del vendedor y del comprador, el VIN, la fecha de venta y las lecturas del odómetro.",
      },
      {
        type: "p",
        text: "Si pagó el impuesto de un vehículo que al final usó 5,000 millas o menos (7,500 si es agropecuario), puede reclamar un crédito en el primer Formulario 2290 del período siguiente — vea [créditos del Formulario 2290](/es/credito-camion-vendido-robado-destruido). Para presentar, siga [la guía paso a paso](/es/como-presentar-formulario-2290-en-linea).",
      },
    ],
    faqs: [
      {
        q: "¿Qué es un vehículo suspendido en el Formulario 2290?",
        a: "Un vehículo que se anticipa usará 5,000 millas o menos en las carreteras públicas durante el período (7,500 si es agropecuario). Se declara en la categoría W y no paga impuesto.",
      },
      {
        q: "¿Tengo que presentar si todos mis vehículos están suspendidos?",
        a: "Sí. Los vehículos de categoría W se tienen que declarar en el Formulario 2290 y aparecen en su Anexo 1 estampado.",
      },
      {
        q: "¿Qué pasa si un vehículo suspendido pasa de 5,000 millas?",
        a: "El impuesto se vuelve exigible. Presente una declaración enmendada a más tardar el último día del mes siguiente al mes en que se excedió el límite.",
      },
      {
        q: "¿Los vehículos suspendidos cuentan para la regla de los 25 vehículos?",
        a: "No. La presentación electrónica obligatoria cuenta los vehículos por los que declara y paga impuesto; los suspendidos no pagan y no cuentan.",
      },
    ],
  },

  // ES — translationOf: form-2290-credit-sold-destroyed-stolen-truck
  {
    slug: "credito-camion-vendido-robado-destruido",
    translationOf: "form-2290-credit-sold-destroyed-stolen-truck",
    category: "credits",
    title: "¿Vendió, le robaron o se destruyó su camión? Cómo reclamar el crédito del Formulario 2290",
    metaTitle: "Crédito 2290 por camión vendido, robado o destruido",
    metaDescription:
      "Si pagó el impuesto 2290 de un camión que vendió o perdió, recupere los meses que no lo usó con un crédito en su próximo Formulario 2290. Requisitos y ejemplo.",
    keywords: [
      "crédito formulario 2290 camión vendido",
      "reembolso 2290 camión vendido",
      "2290 camión robado",
      "2290 camión destruido",
      "devolución impuesto 2290",
    ],
    excerpt:
      "El impuesto del Formulario 2290 se paga por adelantado por todo el período. Si un camión deja de ser suyo a mitad de camino, el impuesto de los meses restantes se recupera como crédito.",
    cta: {
      heading: "Reclame su crédito en su próximo Formulario 2290",
      text: "Agregue el camión, el motivo y la fecha en la pantalla \"Credits\". La aplicación está en inglés.",
      label: "Comenzar mi Formulario 2290",
      to: "/wizard/1",
    },
    publishedAt: "2026-10-07",
    updatedAt: "2026-10-07",
    readingMinutes: 5,
    irsVerified: true,
    sources:
      "Instrucciones para el Formulario 2290(SP) (Rev. julio de 2026) — Línea 5 (créditos), Información que se debe incluir, Cómo calcular el crédito, Cuándo hacer una reclamación; Tabla I (tablas del impuesto para períodos parciales)",
    body: [
      { type: "h2", text: "Quién puede reclamar" },
      {
        type: "p",
        text: "Puede reclamar un crédito por el impuesto pagado sobre un vehículo que:",
      },
      {
        type: "ul",
        items: [
          "Se vendió antes del 1 de junio y no se usó durante el resto del período.",
          "Se destruyó —tan dañado por un accidente u otro hecho fortuito que no sería económico reconstruirlo— antes del 1 de junio y no se usó durante el resto del período.",
          "Fue robado antes del 1 de junio y no se usó durante el resto del período.",
        ],
      },
      {
        type: "callout",
        tone: "warn",
        text: "Las cargas más ligeras no cuentan. El IRS no permite crédito, impuesto reducido ni reembolso por una carga ligera o reducida infrecuente, ni por cambiar o dejar de usar el vehículo.",
      },
      {
        type: "p",
        text: "Un camión que usó 5,000 millas o menos (7,500 si es agropecuario) también da derecho a un crédito, pero sólo después de que termina el período — vea [vehículos suspendidos](/es/vehiculos-suspendidos-categoria-w).",
      },
      { type: "h2", text: "Cómo se calcula" },
      {
        type: "p",
        text: "El crédito es el impuesto que pagó menos el impuesto de los meses en que sí tuvo el camión. Cuente los meses de uso desde el primer día del mes de primer uso en el período hasta el último día del mes en que se vendió, se destruyó o fue robado, y busque esa cantidad de meses en la tabla del IRS para períodos parciales, en la categoría de peso del vehículo.",
      },
      {
        type: "p",
        text: "Un ejemplo con cifras del IRS: un camión de categoría V (más de 75,000 libras) se usó por primera vez en julio, así que pagó el impuesto del período completo. Se vendió en septiembre y el vendedor no lo volvió a usar.",
      },
      {
        type: "table",
        headers: ["Paso", "Cantidad"],
        rows: [
          ["Impuesto pagado (período completo, categoría V)", `$${RATES.V[0].toFixed(2)}`],
          ["Meses de uso: julio, agosto, septiembre", "3 meses"],
          ["Impuesto de 3 meses, categoría V (Tabla I del IRS)", "$137.50"],
          ["Crédito", "$412.50"],
        ],
      },
      {
        type: "p",
        text: "El crédito de cada vehículo se calcula por separado. Los vehículos madereros usan la tabla de vehículos madereros (Tabla II).",
      },
      {
        type: "cta",
        heading: "¿Agrega camiones este mes?",
        text: "Reclame en la misma declaración el crédito del camión que vendió y pague menos hoy. La aplicación está en inglés.",
        label: "Crear su cuenta gratis",
        to: "/login?tab=up",
      },
      { type: "h2", text: "Qué tiene que incluir" },
      {
        type: "ul",
        items: [
          "El VIN.",
          "La categoría del peso bruto tributable.",
          "La fecha de la destrucción, el robo o la venta.",
          "Una copia de la hoja de trabajo del crédito.",
          "Si fue una venta, el nombre y la dirección del comprador.",
        ],
      },
      {
        type: "p",
        text: "Si falta algo, el IRS puede denegar el crédito; el nombre y la dirección del comprador es lo que más se olvida.",
      },
      { type: "h2", text: "Dónde y cuándo reclamarlo" },
      {
        type: "p",
        text: "En el próximo Formulario 2290 que presente, o como reembolso en el Formulario 8849. El crédito no puede ser mayor que el impuesto de la declaración en la que lo reclama; el exceso se reclama como reembolso en el Formulario 8849 y su Anexo 6.",
      },
      {
        type: "p",
        text: "En la aplicación, la pantalla \"Credits\" le pide el VIN, la categoría, el motivo (\"Why are you claiming this credit?\") y la fecha; el crédito se calcula con la tabla del IRS y no puede pasar del impuesto de la declaración. Para el resto de la declaración, vea [cómo presentar por internet](/es/como-presentar-formulario-2290-en-linea) o el detalle completo [en inglés](/blog/form-2290-credit-sold-destroyed-stolen-truck).",
      },
    ],
    faqs: [
      {
        q: "¿Puedo recuperar el impuesto 2290 si vendí mi camión?",
        a: "Sí, si lo vendió antes del 1 de junio y no lo volvió a usar en el período. Reclame un crédito en su próximo Formulario 2290 o un reembolso en el Formulario 8849.",
      },
      {
        q: "¿Cómo calculo el crédito de un camión vendido?",
        a: "Reste del impuesto que pagó el impuesto de los meses que lo usó, según la tabla del IRS. Un camión de categoría V pagado por el período completo y vendido en septiembre tiene 3 meses de uso ($137.50), así que el crédito es $412.50.",
      },
      {
        q: "¿Un camión robado cuenta igual que uno vendido?",
        a: "Sí. Un camión destruido o robado antes del 1 de junio y no usado en el resto del período da el mismo crédito, calculado de la misma forma.",
      },
      {
        q: "¿Y si el crédito es mayor que el impuesto de mi próxima declaración?",
        a: "El crédito no puede exceder el impuesto de esa declaración. El exceso se reclama como reembolso en el Formulario 8849, Anexo 6.",
      },
    ],
  },

  // ES — translationOf: ein-for-form-2290
  {
    slug: "ein-para-formulario-2290",
    translationOf: "ein-for-form-2290",
    category: "basics",
    title: "El EIN para el Formulario 2290: por qué no sirve el número de Seguro Social y cómo obtenerlo",
    metaTitle: "EIN para el Formulario 2290 | No se acepta el SSN",
    metaDescription:
      "Para presentar el Formulario 2290 necesita un EIN; el número de Seguro Social no sirve. Cómo solicitarlo, qué EIN usa una LLC y por qué tiene que coincidir.",
    keywords: [
      "ein para formulario 2290",
      "2290 sin ein",
      "2290 con número de seguro social",
      "número de identificación del empleador 2290",
      "cómo sacar ein para camión",
    ],
    excerpt:
      "La instrucción del IRS es corta: para presentar el Formulario 2290 tiene que tener un EIN. No puede usar su número de Seguro Social, aunque tenga un solo camión.",
    cta: {
      heading: "¿Ya tiene su EIN?",
      text: "Guárdelo una vez en su cuenta y cada declaración empieza desde ahí. La aplicación está en inglés.",
      label: "Crear su cuenta gratis",
      to: "/login?tab=up",
    },
    publishedAt: "2026-10-07",
    updatedAt: "2026-10-07",
    readingMinutes: 4,
    irsVerified: true,
    sources:
      "Instrucciones para el Formulario 2290(SP) (Rev. julio de 2026) — Cómo Comenzar, Número de Identificación del Empleador (EIN), Entidades ignoradas y compañías subsidiarias calificadas conforme al subcapítulo S, Anexo 1 del Formulario 2290; 2290 Business Rules Publication TY2026 v1.1 — R0000-922-01",
    body: [
      {
        type: "p",
        text: "Para presentar el [Formulario 2290](/es/formulario-2290) necesita un número de identificación del empleador (EIN). No puede usar su número de Seguro Social (SSN). Eso aplica igual a un propietario-operador con un solo camión que a una flota.",
      },
      { type: "h2", text: "Cómo obtener un EIN" },
      {
        type: "ul",
        items: [
          "Por internet en IRS.gov/EIN (pulse sobre Español).",
          "Por fax o por correo, con el Formulario SS-4, Solicitud de Número de Identificación del Empleador (EIN).",
          "Por teléfono al 267-941-1099 (la llamada no es gratis), pero sólo si su dirección está en el extranjero, como en México.",
        ],
      },
      {
        type: "callout",
        tone: "info",
        text: "¿Ya tiene un EIN para su nómina u otro fin? Si pertenece a la persona o entidad a cuyo nombre está registrado el camión, ese es el EIN con el que presenta. Asegúrese de que sea la entidad correcta.",
      },
      { type: "h2", text: "LLC de un solo dueño y QSub: su propio EIN" },
      {
        type: "p",
        text: "Si sus camiones están a nombre de una LLC de un solo dueño u otra entidad ignorada elegible, o de una compañía subsidiaria calificada conforme al subcapítulo S (QSub), el IRS la trata como entidad separada para estos impuestos. Tiene que declarar y pagar, y reclamar créditos o reembolsos, bajo el EIN de la entidad, no bajo el número del dueño. Si no sabe si su entidad ya tiene EIN, el IRS indica llamar a su línea de impuestos de negocios y especiales al 800-829-4933.",
      },
      {
        type: "cta",
        heading: "Presente con su EIN",
        text: "La aplicación está en inglés; esta guía le explica cada paso en español.",
        label: "Comenzar mi Formulario 2290",
        to: "/wizard/1",
      },
      { type: "h2", text: "El EIN tiene que coincidir con el IRS" },
      {
        type: "p",
        text: "Una declaración electrónica se compara con la base de datos del IRS: el EIN y el código de control del nombre (\"Name control\") tienen que coincidir con lo que el IRS tiene registrado, o la declaración se rechaza (regla R0000-922-01). Anote el nombre legal y el EIN exactamente como los tiene el IRS, y use el mismo nombre, dirección y EIN en el Anexo 1 que en la declaración.",
      },
      { type: "h2", text: "Cuando ya tenga el EIN" },
      {
        type: "ul",
        items: [
          "Anote el nombre legal, el EIN, el tipo de negocio y la dirección en la pantalla \"Business\".",
          "Agregue cada camión con su VIN, categoría de peso y mes de primer uso.",
          "Pague, firme y transmita. Descargue su [Anexo 1 estampado](/es/anexo-1-formulario-2290) cuando el IRS acepte.",
        ],
      },
      {
        type: "p",
        text: "Todo el proceso está en [la guía paso a paso](/es/como-presentar-formulario-2290-en-linea). Si espera su EIN y se acerca la fecha, revise su plazo con la [calculadora de fechas (en inglés)](/form-2290-due-date-calculator): el plazo no cambia mientras espera el EIN.",
      },
    ],
    faqs: [
      {
        q: "¿Puedo presentar el Formulario 2290 con mi número de Seguro Social?",
        a: "No. El IRS exige un número de identificación del empleador (EIN) para el Formulario 2290. No se puede usar el SSN.",
      },
      {
        q: "¿Cómo saco un EIN para el Formulario 2290?",
        a: "Solicítelo por internet en IRS.gov/EIN, o por fax o correo con el Formulario SS-4. Si su dirección está en el extranjero, como en México, también puede llamar al 267-941-1099.",
      },
      {
        q: "Mis camiones están en una LLC de un solo dueño. ¿Qué EIN uso?",
        a: "El de la LLC. El IRS trata a las entidades ignoradas de un solo dueño elegibles y a las QSub como entidades separadas para estos impuestos; declaran y pagan con su propio EIN.",
      },
      {
        q: "¿Por qué me rechazaron el Formulario 2290 por el EIN?",
        a: "Porque el EIN y el código de control del nombre de la declaración tienen que coincidir con la base de datos del IRS (regla R0000-922-01). Use el nombre legal y el EIN exactamente como los tiene el IRS.",
      },
    ],
  },

  // ES — translationOf: form-2290-for-owner-operators
  {
    slug: "formulario-2290-propietario-operador",
    translationOf: "form-2290-for-owner-operators",
    category: "basics",
    title: "Formulario 2290 para propietarios-operadores: quién presenta, cuánto paga y cómo hacerlo por internet",
    metaTitle: "Formulario 2290 para propietario-operador | Guía",
    metaDescription:
      "Si es dueño y operador de su camión, el Formulario 2290 se presenta cada período. Quién paga en un camión arrendado, cuánto cuesta y cuándo vence.",
    keywords: [
      "formulario 2290 propietario operador",
      "owner operator 2290 en español",
      "2290 camión arrendado",
      "cuánto cuesta el 2290 de un tráiler",
      "2290 para camioneros",
    ],
    excerpt:
      "Si su camión es suyo y usted lo trabaja, el Formulario 2290 es una declaración que presenta cada período, casi siempre a más tardar el 31 de agosto. Esto es lo que necesita saber.",
    cta: {
      heading: "Presente el 2290 de su camión",
      text: "Con su peso y su mes de primer uso, termina en una sesión. La aplicación está en inglés.",
      label: "Comenzar mi Formulario 2290",
      to: "/wizard/1",
    },
    publishedAt: "2026-10-07",
    updatedAt: "2026-10-07",
    readingMinutes: 5,
    irsVerified: true,
    sources:
      "Instrucciones para el Formulario 2290(SP) (Rev. julio de 2026) — Quién Tiene que Presentar el Formulario, Vehículo registrado en nombre de dos personas, Peso Bruto Tributable, Cuándo se Tiene que Presentar el Formulario, Registros; Formulario 2290, página 2",
    body: [
      { type: "h2", text: "¿Tiene que presentar?" },
      {
        type: "p",
        text: "Sí, si un vehículo motorizado tributable está registrado, o tiene que estar registrado, a su nombre cuando se usa por primera vez en el período, y tiene un peso bruto tributable de 55,000 libras o más. No importa si opera como persona física, LLC o sociedad anónima. Necesita un EIN — vea [el EIN para el Formulario 2290](/es/ein-para-formulario-2290).",
      },
      {
        type: "p",
        text: "El peso bruto tributable no es el peso vacío: es el camión totalmente equipado, más los remolques que normalmente jala, más la carga máxima que normalmente lleva. Un tractor con remolque cargado por lo general cae en una categoría alta.",
      },
      { type: "h2", text: "¿Arrendado a un transportista? Quién paga" },
      {
        type: "p",
        text: "La regla del IRS para un vehículo registrado en nombre de dos personas: si está registrado a nombre del propietario y de otra persona, el propietario es quien tiene que pagar el impuesto. La misma regla se aplica a un vehículo arrendado registrado a nombre de dos personas.",
      },
      {
        type: "p",
        text: "Así que si el camión es suyo y está registrado a su nombre y al del transportista, el impuesto es suyo. Guarde su propio [Anexo 1 estampado](/es/anexo-1-formulario-2290).",
      },
      { type: "h2", text: "Cuánto paga su camión" },
      {
        type: "p",
        text: `Depende de la categoría de peso y del mes de primer uso en el período de julio a junio. Un camión de más de 75,000 libras (categoría V) en servicio desde julio paga $${RATES.V[0].toFixed(2)} por el período completo, la cantidad anual más alta del formulario. Un camión maderero de la misma categoría paga $${RATES.V[1].toFixed(2)}. Uno que empieza a usarse después de julio paga sólo los meses que quedan. Vea su cifra exacta en la [calculadora del impuesto (en inglés)](/form-2290-tax-calculator).`,
      },
      {
        type: "callout",
        tone: "info",
        text: "¿Va a recorrer 5,000 millas o menos este período (7,500 si es agropecuario)? Declare el camión como [suspendido, categoría W](/es/vehiculos-suspendidos-categoria-w). No paga impuesto, pero presenta igual.",
      },
      { type: "h2", text: "Cuándo vence" },
      {
        type: "p",
        text: "El último día del mes siguiente al mes en que el camión se usó por primera vez en una carretera pública. Si ya trabajaba en julio, vence el 31 de agosto; si lo compró y lo manejó por primera vez en noviembre, vence el 31 de diciembre. El plazo no depende de la renovación de su registro. Vea [todas las fechas](/es/fecha-de-vencimiento-formulario-2290).",
      },
      {
        type: "cta",
        heading: "¿Ya sabe su peso y su mes?",
        text: "Crear la cuenta es gratis; sólo paga cuando presenta. La aplicación está en inglés; esta guía le explica cada paso.",
        label: "Crear su cuenta gratis",
        to: "/login?tab=up",
      },
      { type: "h2", text: "Cómo presenta por internet" },
      {
        type: "ul",
        items: [
          "Datos del negocio: nombre legal, EIN, dirección y código de control del nombre.",
          "Tipo de declaración: \"Form2290\".",
          "Su camión: VIN, categoría de peso y mes de primer uso. Márquelo como maderero o suspendido si aplica.",
          "Si vendió un camión por el que ya pagó, reclame el [crédito](/es/credito-camion-vendido-robado-destruido).",
          "Revise el impuesto y páguelo por retiro electrónico de fondos, EFTPS o tarjeta — vea [cómo pagar](/es/como-pagar-formulario-2290).",
          "Firme y transmita, y descargue su Anexo 1 estampado cuando el IRS acepte.",
        ],
      },
      { type: "h2", text: "Después de presentar" },
      {
        type: "ul",
        items: [
          "Lleve el Anexo 1 estampado al registrar o renovar.",
          "¿Escribió mal un VIN? Presente una [corrección del VIN](/es/correccion-de-vin-formulario-2290).",
          "Guarde sus registros al menos 3 años después de la fecha en que se adeudó o se pagó el impuesto, lo que ocurra más tarde.",
        ],
      },
    ],
    faqs: [
      {
        q: "¿Un propietario-operador tiene que presentar el Formulario 2290?",
        a: "Sí, si el camión está registrado (o tiene que estar registrado) a su nombre cuando se usa por primera vez en el período y su peso bruto tributable es de 55,000 libras o más.",
      },
      {
        q: "¿Quién paga el 2290 de un camión arrendado?",
        a: "Si el vehículo está registrado a nombre del propietario y de otra persona, el propietario tiene que pagar el impuesto. La regla también se aplica a un vehículo arrendado registrado a nombre de dos personas.",
      },
      {
        q: "¿Cuánto cuesta el 2290 de un tráiler?",
        a: "Depende de la categoría de peso y del mes de primer uso. Un camión de más de 75,000 libras en servicio desde julio paga la cantidad anual más alta del formulario; si se usa por primera vez después, paga menos.",
      },
      {
        q: "¿Puedo presentar con mi número de Seguro Social?",
        a: "No. El Formulario 2290 exige un número de identificación del empleador (EIN).",
      },
    ],
  },

  // ES — translationOf: how-to-pay-form-2290
  {
    slug: "como-pagar-formulario-2290",
    translationOf: "how-to-pay-form-2290",
    category: "basics",
    title: "Cómo pagar el impuesto del Formulario 2290: retiro electrónico, EFTPS, tarjeta o cheque",
    metaTitle: "Cómo pagar el Formulario 2290: débito, EFTPS o tarjeta",
    metaDescription:
      "Las cuatro formas de pagar el impuesto del Formulario 2290 según el IRS: retiro electrónico de fondos, EFTPS, tarjeta de crédito o débito, y cheque o giro.",
    keywords: [
      "cómo pagar el 2290",
      "pagar formulario 2290",
      "pagar 2290 con tarjeta",
      "eftps 2290 en español",
      "débito directo 2290",
    ],
    excerpt:
      "La regla detrás de cada opción: el impuesto se paga completo con el Formulario 2290. El IRS ofrece cuatro maneras de hacerlo.",
    cta: {
      heading: "Pague y presente en una sola sesión",
      text: "Escoja su forma de pago en la pantalla \"Pay\". La aplicación está en inglés.",
      label: "Comenzar mi Formulario 2290",
      to: "/wizard/1",
    },
    publishedAt: "2026-10-07",
    updatedAt: "2026-10-07",
    readingMinutes: 5,
    irsVerified: true,
    sources:
      "Instrucciones para el Formulario 2290(SP) (Rev. julio de 2026) — Cómo Pagar el Impuesto (Retiro electrónico de fondos, EFTPS, Pagos hechos a tiempo, Tarjeta de crédito o débito, Cheque o giro); 2290 Business Rules Publication TY2026 v1.1 — FPYMT-076-02, R0000-906-03, F2290-068-01",
    body: [
      {
        type: "p",
        text: "El IRS es claro: tiene que pagar el impuesto en su totalidad al presentar el [Formulario 2290](/es/formulario-2290). Hay cuatro métodos:",
      },
      {
        type: "table",
        headers: ["Método", "Cómo funciona", "¿Con declaración electrónica?"],
        rows: [
          ["Retiro electrónico de fondos (débito directo)", "Usted autoriza un débito de su cuenta bancaria dentro de la declaración electrónica.", "Sí"],
          ["Sistema de pago electrónico del impuesto federal (EFTPS)", "Paga por separado en EFTPS y marca el recuadro EFTPS en la declaración.", "Sí"],
          ["Tarjeta de crédito o débito", "Paga con un proveedor aprobado en IRS.gov/PagueConTarjeta y marca el recuadro de tarjeta.", "Sí"],
          ["Cheque o giro", "Lo envía por correo con el Formulario 2290-V, Comprobante de Pago.", "No: sólo pago en papel"],
        ],
      },
      { type: "h2", text: "Retiro electrónico de fondos (débito directo)" },
      {
        type: "p",
        text: "Sólo si presenta electrónicamente. Anota el número de ruta bancaria (\"Routing number\"), el número de cuenta (\"Account number\") y el tipo de cuenta (\"Account type\": cheques o ahorros), y el débito va dentro de la declaración, así que no envía comprobante de pago.",
      },
      {
        type: "p",
        text: "Una regla del IRS importa aquí: la fecha solicitada para el pago tiene que ser igual a la fecha en que el IRS recibe la declaración (FPYMT-076-02); no la escoge usted, se fija al enviar. Un número de ruta que el IRS no reconoce también se rechaza (R0000-906-03), así que confírmelo con su banco.",
      },
      { type: "h2", text: "EFTPS" },
      {
        type: "ul",
        items: [
          "Es voluntario, pero tiene que inscribirse antes de usarlo: en EFTPS.gov o al 800-244-4829 (24 horas, 7 días). Información en español en IRS.gov/EFTPS, pulsando sobre Español.",
          "Para que el pago llegue a tiempo, hágalo a más tardar a las 8:00 p.m., hora del Este, el día antes de la fecha de vencimiento.",
          "Marque el recuadro EFTPS en la línea 6 del Formulario 2290 y no incluya el comprobante de pago.",
          "El EFTPS no procesa pagos de instituciones financieras internacionales.",
        ],
      },
      {
        type: "cta",
        heading: "¿Listo para presentar y pagar?",
        text: "El impuesto se muestra antes de que usted confirme. Crear la cuenta es gratis; la aplicación está en inglés.",
        label: "Crear su cuenta gratis",
        to: "/login?tab=up",
      },
      { type: "h2", text: "Tarjeta de crédito o débito" },
      {
        type: "p",
        text: "Pague en IRS.gov/PagueConTarjeta. Estos proveedores cobran un cargo por servicio, aparte del impuesto. Marque el recuadro de tarjeta de crédito o débito en la línea 6 y no incluya el comprobante de pago.",
      },
      { type: "h2", text: "Cheque o giro" },
      {
        type: "ul",
        items: [
          "A nombre de \"United States Treasury\". No envíe efectivo.",
          "Escriba en el pago su nombre, dirección, EIN, \"Form 2290\" y la fecha del recuadro 3 del comprobante.",
          "Llene el Formulario 2290-V y envíelo con el pago, sin engraparlos.",
          "Si presentó electrónicamente, no envíe el Formulario 2290 ni el Anexo 1 con el comprobante.",
        ],
      },
      { type: "h2", text: "Si no debe nada" },
      {
        type: "p",
        text: "Una declaración sin saldo —por ejemplo, sólo con [vehículos suspendidos, categoría W](/es/vehiculos-suspendidos-categoria-w), o con créditos que cubren el impuesto— no necesita método de pago. Pero una declaración con saldo y sin método de pago se rechaza (F2290-068-01).",
      },
      { type: "h2", text: "Al pagar en la aplicación" },
      {
        type: "ul",
        items: [
          "Revise el impuesto en \"Tax summary\", o calcúlelo antes con la [calculadora (en inglés)](/form-2290-tax-calculator).",
          "En \"Pay\", escoja \"EFW\", \"EFTPS\" o \"Credit or debit card\". Esta aplicación no ofrece pago con cheque.",
          "Para EFW, anote el número de ruta, el número de cuenta y el tipo de cuenta.",
          "Firme y transmita. Cuando el IRS acepte, su [Anexo 1 estampado](/es/anexo-1-formulario-2290) está listo.",
        ],
      },
    ],
    faqs: [
      {
        q: "¿Cómo pago el impuesto del Formulario 2290 por internet?",
        a: "Con retiro electrónico de fondos (débito directo) al presentar electrónicamente, con EFTPS, o con tarjeta de crédito o débito en IRS.gov/PagueConTarjeta. El impuesto se paga completo con la declaración.",
      },
      {
        q: "¿Puedo pagar el 2290 con tarjeta de crédito?",
        a: "Sí, con un proveedor aprobado en IRS.gov/PagueConTarjeta, que cobra un cargo por servicio. Marque el recuadro de tarjeta en la línea 6.",
      },
      {
        q: "¿Cuál es la hora límite de EFTPS para el 2290?",
        a: "Haga el pago a más tardar a las 8:00 p.m., hora del Este, el día antes de la fecha de vencimiento. Tiene que estar inscrito en EFTPS antes de usarlo.",
      },
      {
        q: "¿Qué diferencia hay entre EFTPS y el retiro electrónico?",
        a: "El retiro electrónico es un débito que autoriza dentro de la declaración electrónica. EFTPS es un sistema aparte en el que se inscribe y paga usted mismo; luego marca el recuadro EFTPS en la declaración.",
      },
    ],
  },
];

export const getPostEs = (slug) => BLOG_POSTS_ES.find((p) => p.slug === slug);

/** Spanish slug for an English post, or undefined if there's no translation. */
export const esSlugFor = (englishSlug) => BLOG_POSTS_ES.find((p) => p.translationOf === englishSlug)?.slug;

export const sortedPostsEs = () =>
  [...BLOG_POSTS_ES].sort((a, b) => (a.publishedAt < b.publishedAt ? 1 : -1));

export const relatedPostsEs = (slug, limit = 3) => {
  const post = getPostEs(slug);
  if (!post) return [];
  const same = BLOG_POSTS_ES.filter((p) => p.slug !== slug && p.category === post.category);
  const others = BLOG_POSTS_ES.filter((p) => p.slug !== slug && p.category !== post.category);
  return [...same, ...others].slice(0, limit);
};
